import type { EditorGameAreaView } from 'editor/EditorGameAreaView';
import { MeasureTapeBlockerModel } from 'game/boardMVC/models/blockers/MeasureTapeBlockerModel';
import type { BoardModel } from 'game/boardMVC/models/board/BoardModel';
import type { TileStackModel } from 'game/boardMVC/models/board/TileStackModel';
import type { ConnectedBlockerView } from 'game/boardMVC/types';
import { isConnectedBlocker } from 'game/boardMVC/utils/isConnectedBlocker';
import type { BoardViewFactory } from 'game/boardMVC/views/board/BoardViewFactory';
import MathUtils from 'utils/MathUtils';

export function createConnections(
  editorAreaView: EditorGameAreaView,
  stackModel: TileStackModel,
  previousStackModel: TileStackModel,
): void {
  if (stackModel) {
    const model = stackModel;
    const previousModel = previousStackModel;

    if (
      model.blocker &&
      isConnectedBlocker(model.blocker) &&
      previousModel.blocker &&
      isConnectedBlocker(previousModel.blocker)
    ) {
      const { viewFactory } = editorAreaView;
      model.blocker.connection.add(previousModel);
      previousModel.blocker.connection.add(model);

      if (model.blocker instanceof MeasureTapeBlockerModel) {
        model.blocker.isCase = false;
      }

      // update all connected blockers after adding a connection
      model.getConnectedBlockerStacks().forEach((stack) => {
        if (stack.blocker && isConnectedBlocker(stack.blocker)) {
          const blockerView = viewFactory.getBlockerView(stack.blocker.id);
          (blockerView as ConnectedBlockerView).updateConnections();
        }
      });
    }
  }
}

export function removeConnections(
  boardModel: BoardModel,
  editorAreaView: EditorGameAreaView,
  stackModel: TileStackModel,
): void {
  if (stackModel.blocker && isConnectedBlocker(stackModel.blocker)) {
    const { viewFactory } = editorAreaView;
    const connections = stackModel.blocker.connection.clearAll();

    removeMeasureTapeConnections(stackModel, viewFactory);

    connections.forEach((pos) => {
      const connectedBlocker = boardModel.getStack(pos.x, pos.y)?.blocker;
      if (connectedBlocker && isConnectedBlocker(connectedBlocker)) {
        connectedBlocker.connection.remove(stackModel);

        const blockerView = viewFactory.getBlockerView(connectedBlocker.id);
        (blockerView as ConnectedBlockerView).updateConnections();
      }
    });

    const blockerView = viewFactory.getBlockerView(stackModel.blocker.id);
    (blockerView as ConnectedBlockerView).updateConnections();
  }
}

/**
 * Removes a measure tape blocker from a chain of blockers.
 * Checks if the blocker is a case (root) or part of the blade and updates the connections accordingly.
 **/
function removeMeasureTapeConnections(stackModel: TileStackModel, viewFactory: BoardViewFactory): void {
  if (stackModel.blocker instanceof MeasureTapeBlockerModel) {
    stackModel.blocker.isCase = true;

    const stacksToUpdate: TileStackModel[] = [];

    stackModel.blocker.chainStates.forEach((chainState) => {
      const index = chainState.tileStacks.findIndex((tileStack) => tileStack === stackModel);
      if (index === -1) return;

      const successors = chainState.tileStacks.slice(index + 1);
      let predecessor = stackModel;

      successors.some((successor) => {
        const successorBlocker = successor.blocker as MeasureTapeBlockerModel;
        const predecessorBlocker = predecessor.blocker as MeasureTapeBlockerModel;

        stacksToUpdate.push(...[successor, predecessor]);

        successorBlocker.connection.remove(predecessor);
        predecessorBlocker.connection.remove(successor);

        if (successorBlocker.chainStates.length === 1) {
          successorBlocker.isCase = true;
          return true;
        }
        successorBlocker.isCase = false;

        predecessor = successor;
        return false;
      });
    });

    stacksToUpdate.forEach((stack) => {
      if (stack.blocker && isConnectedBlocker(stack.blocker)) {
        const blockerView = viewFactory.getBlockerView(stack.blocker.id);
        (blockerView as ConnectedBlockerView).updateConnections();
      }
    });
  }
}

/**
 * Validates the connection conditions between two measure tape blockers.
 * - A case blocker can only have one connection.
 * - A chain can only connect to another chain with the same direction.
 * - A non-case blocker can only connect to a case or chain following the same direction.
 * - A chain cannot connect to another chain with the opposite direction.
 */
export function validateMeasureTapeConnectionConditions(
  currentStackModel: TileStackModel,
  previousStackModel: TileStackModel,
): boolean {
  // if the blockers are not measure tape blockers, no need to validate
  if (
    !(previousStackModel.blocker instanceof MeasureTapeBlockerModel) ||
    !(currentStackModel.blocker instanceof MeasureTapeBlockerModel)
  ) {
    return true;
  }

  const previousBlocker = previousStackModel.blocker;
  const currentBlocker = currentStackModel.blocker;

  const direction = previousStackModel.getDirectionTo(currentStackModel);

  if (previousBlocker.isCase && previousBlocker.connection.length === 1) {
    console.warn('ConnectionUtils: Case blocker can only have one connection.');
    return false;
  }

  if (
    currentBlocker.isCase &&
    currentBlocker.connection.length === 1 &&
    !currentBlocker.chainStates.every((chainState) => MathUtils.comparePoints(chainState.direction, direction))
  ) {
    console.warn('ConnectionUtils: Cannot connect a chain to another chain with a different direction.');
    return false;
  }

  if (
    !previousBlocker.isCase &&
    !previousBlocker.chainStates.some((chainState) => MathUtils.comparePoints(chainState.direction, direction))
  ) {
    console.warn(
      'ConnectionUtils: Cannot connect a non-case blocker to a chain or blocker following a different direction.',
    );
    return false;
  }

  if (
    currentBlocker.chainStates.some((chainState) => {
      const compare = MathUtils.comparePoints(
        chainState.direction,
        MathUtils.getInverseVector(direction.x, direction.y),
      );
      return compare;
    })
  ) {
    console.warn('ConnectionUtils: Cannot connect a chain to another chain with the opposite direction.');
    return false;
  }

  return true;
}
